import { TextField } from "@dash/form";
import type { FC } from "react";
import { useMemo } from "react";

import type { TicketCategory } from "@megaron/support-contracts";

import { SupportTicketLinkedInvoice } from "../invoice/SupportTicketLinkedInvoice";
import {
  SupportTicketProductsCreateField,
  TicketProductDraft,
} from "../products/SupportTicketProductsField";
import { SupportSelect, SupportSelectOption } from "../shared/SupportSelect";
import { SupportTicketCategorySelect } from "../shared/SupportTicketCategorySelect";
import { supportRequiredFieldLabel } from "../shared/supportRequiredFieldLabel";
import { SupportTicketReasonPicker } from "./SupportTicketReasonPicker";
import { supportFormFieldCss } from "./supportCreateTicketFormStyles";

type ContactOption = {
  label: string;
  value: string;
  email: string;
  name: string;
};

type MailboxOption = {
  label: string;
  value: string;
};

type Props = {
  message: string;
  setMessage: (value: string) => void;
  reporterName: string;
  setReporterName: (value: string) => void;
  reporterEmail: string;
  setReporterEmail: (value: string) => void;
  selectedContact: string;
  onContactChange: (value: string) => void;
  sourceMailbox: string;
  setSourceMailbox: (value: string) => void;
  mailboxOptions: MailboxOption[];
  isMailboxLoading: boolean;
  contactOptions: ContactOption[];
  hasCustomerContext: boolean;
  selectedReasonIds: string[];
  setSelectedReasonIds: (value: string[]) => void;
  category: TicketCategory | null;
  setCategory: (value: TicketCategory | null) => void;
  products: TicketProductDraft[];
  setProducts: (value: TicketProductDraft[]) => void;
  invoiceNumber: string;
  setInvoiceNumber: (value: string) => void;
};

export const SupportCreateTicketFormFields: FC<Props> = ({
  message,
  setMessage,
  reporterName,
  setReporterName,
  reporterEmail,
  setReporterEmail,
  selectedContact,
  onContactChange,
  sourceMailbox,
  setSourceMailbox,
  mailboxOptions,
  isMailboxLoading,
  contactOptions,
  hasCustomerContext,
  selectedReasonIds,
  setSelectedReasonIds,
  category,
  setCategory,
  products,
  setProducts,
  invoiceNumber,
  setInvoiceNumber,
}) => {
  const mailboxSelectOptions = useMemo<SupportSelectOption<string>[]>(
    () => [
      { value: null, label: "Bez skrzynki (tylko w aplikacji)" },
      ...mailboxOptions.map((mailbox) => ({ value: mailbox.value, label: mailbox.label })),
    ],
    [mailboxOptions],
  );

  const contactSelectOptions = useMemo<SupportSelectOption<string>[]>(
    () => contactOptions.map((option) => ({ value: option.value, label: option.label })),
    [contactOptions],
  );

  return (
    <>
      <SupportTicketReasonPicker
        selectedReasonIds={selectedReasonIds}
        onSelectedReasonIdsChange={setSelectedReasonIds}
      />
      <SupportTicketCategorySelect variant="form" label="Kategoria" value={category} onChange={setCategory} />
      <SupportSelect<string>
        variant="form"
        label="Skrzynka odpowiedzi"
        placeholder="Bez skrzynki (tylko w aplikacji)"
        value={sourceMailbox || null}
        options={mailboxSelectOptions}
        onChange={(value) => setSourceMailbox(value ?? "")}
        isDisabled={isMailboxLoading}
      />
      {hasCustomerContext ? (
        <SupportSelect<string>
          variant="form"
          label={supportRequiredFieldLabel("Kontakt")}
          placeholder="Wybierz kontakt..."
          value={selectedContact || null}
          options={contactSelectOptions}
          onChange={(value) => onContactChange(value ?? "")}
          isDisabled={contactOptions.length === 0}
        />
      ) : (
        <>
          <TextField
            label="Email kontaktu"
            value={reporterEmail}
            onChange={setReporterEmail}
            css={supportFormFieldCss}
          />
          <TextField
            label="Nazwa kontaktu"
            value={reporterName}
            onChange={setReporterName}
            css={supportFormFieldCss}
          />
        </>
      )}
      <SupportTicketProductsCreateField value={products} onChange={setProducts} />
      <TextField
        label="Numer faktury"
        value={invoiceNumber}
        onChange={setInvoiceNumber}
        css={supportFormFieldCss}
      />
      <SupportTicketLinkedInvoice invoiceNumber={invoiceNumber} />
      <TextField
        label={supportRequiredFieldLabel("Wiadomość")}
        value={message}
        onChange={setMessage}
        multiline
        css={supportFormFieldCss}
      />
    </>
  );
};
